// Pixel rendering, ported from the prototypes. Two sources of avatar:
//   • real sprite_data (12×12 of . 1 2 3) when we have it, and
//   • a deterministic hash-avatar seeded on a stable string, for players whose
//     sprite_data the API does not currently expose (see the roster gap in
//     build/HANDOFF-frontend-gaps.md). The hash art is the same greyscale
//     algorithm the submit screen uses for album thumbnails, so the look holds.

import type { MediaType } from './types';

const CELL = 'g';

// ---- real 12×12 sprites ---------------------------------------------------

function classForChar(ch: string): string | null {
  if (ch === '1') return 'c1';
  if (ch === '2') return 'c2';
  if (ch === '3') return 'c3';
  return null; // '.' → transparent
}

export function SpriteFace({ rows, size = 36 }: { rows: string[]; size?: number }) {
  const cells: JSX.Element[] = [];
  rows.forEach((row, y) => {
    for (const ch of row.slice(0, 12)) {
      const cls = classForChar(ch);
      cells.push(<i key={`${y}-${cells.length}`} className={cls ?? undefined} />);
    }
  });
  return (
    <div className="mug">
      <div className="face" style={{ width: size, height: size }}>
        {cells}
      </div>
    </div>
  );
}

// ---- deterministic hash-avatar (fallback) ---------------------------------

function fnv(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** A mirrored n×n greyscale grid, stable for a given seed. */
export function HashAvatar({ seed, size = 36 }: { seed: string; size?: number }) {
  const n = 8;
  let h = fnv(seed);
  const half = Math.ceil(n / 2);
  const grid: number[][] = [];
  for (let y = 0; y < n; y++) {
    grid.push([]);
    for (let x = 0; x < half; x++) {
      // Math.imul keeps the multiply in 32-bit range (a plain `*` overflows JS
      // float precision and collapses the low bits to 0 → an all-black avatar).
      // Take the high bits — an LCG's low bits are poorly distributed.
      h = (Math.imul(h, 1103515245) + 12345) >>> 0;
      grid[y][x] = (h >>> 28) & 3;
    }
  }
  const cells: JSX.Element[] = [];
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const v = grid[y][x < half ? x : n - 1 - x];
      cells.push(<i key={`${y}-${x}`} className={`${CELL}${v}`} />);
    }
  }
  return (
    <div className="mug">
      <div
        className="art"
        style={{ width: size, height: size, gridTemplateColumns: `repeat(${n},1fr)`, gridTemplateRows: `repeat(${n},1fr)` }}
      >
        {cells}
      </div>
    </div>
  );
}

/** Prefer real sprite_data; fall back to the hash-avatar. */
export function Avatar({
  spriteData,
  seed,
  size = 36,
}: {
  spriteData?: string[] | null;
  seed: string;
  size?: number;
}) {
  if (spriteData && spriteData.length === 12) return <SpriteFace rows={spriteData} size={size} />;
  return <HashAvatar seed={seed} size={size} />;
}

// ---- media-type glyph -----------------------------------------------------

const MEDIA: Record<MediaType, string[]> = {
  audio: ['...11...', '...111..', '...1.11.', '...1..1.', '..11....', '.111....', '..11....', '........'],
  image: ['11111111', '1......1', '1..11..1', '1.1111.1', '111111.1', '1......1', '11111111', '........'],
  video: ['..1..1..', '...11...', '11111111', '1......1', '1.1111.1', '1......1', '11111111', '..1..1..'],
};

export function MediaGlyph({ type, size = 16 }: { type: MediaType; size?: number }) {
  const rows = MEDIA[type] || MEDIA.audio;
  const cells: JSX.Element[] = [];
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) =>
      cells.push(<i key={`${y}-${x}`} className={ch === '1' ? 'on' : undefined} />),
    ),
  );
  return (
    <span className="mtype" style={{ width: size, height: size }} aria-hidden="true">
      {cells}
    </span>
  );
}
